"use client"

import { trpc } from "@plura/shared/trpc";
import { useEffect, useRef } from "react";

const VIEWER_ID_KEY = "formViewerId";

/**
 * A stable, anonymous id for this browser, persisted in localStorage. Lets the
 * server dedupe views by `(collectionId, viewerId)` so the same person viewing
 * a form repeatedly only ever counts once. It's a random id — never PII.
 */
function getOrCreateViewerId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let id = window.localStorage.getItem(VIEWER_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(VIEWER_ID_KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled — skip tracking rather than throw.
    return null;
  }
}

interface Options {
  collectionId: string;
  country?: string | null;
  /** Skip tracking entirely (e.g. editor / preview render paths). */
  enabled?: boolean;
  /** Fraction of the element that must be visible to count. Default 0.5. */
  threshold?: number;
  /** How long it must stay visible before counting, in ms. Default 1000. */
  dwellMs?: number;
}

/**
 * Fire-and-forget unique-view tracking for a published form.
 *
 * Returns a ref to attach to the form's root element. A view is recorded only
 * once that element has been continuously visible on screen for a short dwell,
 * so a form embedded below the fold that's never scrolled into view is not
 * counted (the explicit ask). It fires at most once per mount; server-side
 * dedup handles repeat page loads, and the mutation's errors are swallowed so
 * tracking can never break a live form.
 *
 * Note: inside a cross-origin iframe the observer sees the iframe's own
 * viewport, not the host page's scroll position — so an iframed full-screen
 * form counts on load. Lazy-mounting the iframe is the embed loader's job.
 */
export function useFormViewTracking<T extends HTMLElement = HTMLDivElement>({
  collectionId,
  country,
  enabled = true,
  threshold = 0.5,
  dwellMs = 1000,
}: Options) {
  const ref = useRef<T | null>(null);
  const reportedRef = useRef(false);

  // Keep a live handle to the mutation without making it an effect dep
  // (re-subscribing the observer on every render would reset the dwell timer).
  const trackFormView = trpc.leadForm.trackFormView.useMutation();
  const mutateRef = useRef(trackFormView.mutate);
  mutateRef.current = trackFormView.mutate;

  useEffect(() => {
    if (!enabled || !collectionId) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;

    const report = () => {
      if (reportedRef.current) return;
      reportedRef.current = true;
      const viewerId = getOrCreateViewerId();
      if (!viewerId) return;
      // Best-effort: ignore the result entirely. The server also swallows
      // failures; this onError just stops React Query surfacing them.
      mutateRef.current(
        { collectionId, viewerId, country: country ?? undefined },
        { onError: () => undefined }
      );
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && entry.intersectionRatio >= threshold) {
          if (!dwellTimer && !reportedRef.current) {
            dwellTimer = setTimeout(() => {
              report();
              observer.disconnect();
            }, dwellMs);
          }
        } else if (dwellTimer) {
          // Scrolled away before the dwell elapsed — reset, don't count.
          clearTimeout(dwellTimer);
          dwellTimer = null;
        }
      },
      { threshold: [0, threshold, 1] }
    );

    observer.observe(el);

    return () => {
      if (dwellTimer) clearTimeout(dwellTimer);
      observer.disconnect();
    };
  }, [collectionId, country, enabled, threshold, dwellMs]);

  return ref;
}
